import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Eye, EyeOff, Users, ChevronDown, ChevronUp, Briefcase, Mail, Phone } from "lucide-react";
import NotifyEmailField from "@/components/NotifyEmailField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SECTIONS = [
  { key: "intro", title_de: "Ihre Chance", title_en: "Your Opportunity", content_de: "Wir bieten haupt- und nebenberuflich eine Zusammenarbeit auf Basis gewerblicher Tätigkeit an.", content_en: "We offer full-time and part-time collaboration on a freelance basis." },
  { key: "offer", title_de: "Wir bieten", title_en: "We Offer", content_de: "Eigener Geschäftsaufbau\nEigene Ausbildungsakademie für Schulungen in Persönlichkeit und Fachwissen\nIntensive Einarbeitungsphase\nRegelmäßige Weiterbildungsmöglichkeiten", content_en: "Build your own business\nIn-house training academy for personal development and expertise\nIntensive onboarding phase\nRegular training opportunities" },
  { key: "requirements", title_de: "Was Sie mitbringen", title_en: "What You Bring", content_de: "Alter über 18 Jahre\nFührerschein Klasse B\nFreude an Akquise, Vertrieb und der Arbeit mit Interessenten und Kunden\nBegeisterung für neue Themen\nVerantwortungsbewusstsein\nVorausschauendes Denken und Handeln", content_en: "Age 18 or older\nValid driver's license (Class B)\nEnjoyment of acquisition, sales, and working with prospects and customers\nEnthusiasm for new topics\nSense of responsibility\nForward-thinking approach" },
];

export default function AdminJobsTab() {
  const [jobs, setJobs] = useState([]);
  const [karriereVisible, setKarriereVisible] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title_de: "", title_en: "", sections: [...DEFAULT_SECTIONS], status: "draft", publish_date: "", end_date: "" });
  const [expandedJob, setExpandedJob] = useState(null);
  const [applications, setApplications] = useState({});

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/jobs/all`, { withCredentials: true });
      setJobs(data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/karriere`);
      setKarriereVisible(data.visible);
    } catch (err) { console.error(err); }
  }, []);

  const fetchApps = useCallback(async (jobId) => {
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/applications`, { withCredentials: true });
      setApplications(prev => ({ ...prev, [jobId]: data }));
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchJobs(); fetchSettings(); }, [fetchJobs, fetchSettings]);

  const toggleVisibility = async () => {
    const nv = !karriereVisible;
    await axios.put(`${API}/settings/karriere`, { visible: nv }, { withCredentials: true });
    setKarriereVisible(nv);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title_de: "", title_en: "", sections: [...DEFAULT_SECTIONS.map(s => ({...s}))], status: "draft", publish_date: new Date().toISOString().split("T")[0], end_date: "" });
    setDialogOpen(true);
  };

  const openEdit = (job) => {
    setEditing(job);
    setForm({ title_de: job.title_de, title_en: job.title_en, sections: job.sections || [...DEFAULT_SECTIONS], status: job.status, publish_date: job.publish_date || "", end_date: job.end_date || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/jobs/${editing.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/jobs`, form, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchJobs();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Stellenangebot wirklich löschen?")) return;
    await axios.delete(`${API}/jobs/${id}`, { withCredentials: true });
    fetchJobs();
  };

  const updateSection = (idx, field, value) => {
    const s = [...form.sections];
    s[idx] = { ...s[idx], [field]: value };
    setForm({ ...form, sections: s });
  };

  const addSection = () => {
    setForm({ ...form, sections: [...form.sections, { key: `custom_${Date.now()}`, title_de: "", title_en: "", content_de: "", content_en: "" }] });
  };

  const removeSection = (idx) => {
    const s = form.sections.filter((_, i) => i !== idx);
    setForm({ ...form, sections: s });
  };

  const toggleExpand = (id) => {
    if (expandedJob === id) { setExpandedJob(null); }
    else { setExpandedJob(id); if (!applications[id]) fetchApps(id); }
  };

  const statusLabel = { draft: "Entwurf", published: "Veröffentlicht" };
  const statusColor = { draft: "text-yellow-400", published: "text-green-400" };
  const today = new Date().toISOString().split("T")[0];

  return (
    <div>
      <NotifyEmailField service="jobs" label="Empfänger für Bewerbungs-Benachrichtigungen" />
      {/* Karriere visibility toggle */}
      <div className="flex items-center justify-between mb-6 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4">
        <div>
          <p className="text-white text-sm font-medium">Menüeintrag "Karriere"</p>
          <p className="text-[#3A4F60] text-xs">Wird nur angezeigt, wenn aktiviert UND aktive Stellen vorhanden</p>
        </div>
        <Button onClick={toggleVisibility} variant="ghost" className={karriereVisible ? "text-green-400" : "text-red-400"}>
          {karriereVisible ? <><Eye className="w-4 h-4 mr-1" /> Sichtbar</> : <><EyeOff className="w-4 h-4 mr-1" /> Ausgeblendet</>}
        </Button>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading text-xl text-white">Stellenangebote</h3>
        <Button data-testid="create-job-button" onClick={openCreate} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">
          <Plus className="w-4 h-4 mr-1" /> Neue Stelle
        </Button>
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-12 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg">
          <Briefcase className="w-10 h-10 text-[#3A4F60] mx-auto mb-3" />
          <p className="text-[#3A4F60] text-sm">Noch keine Stellenangebote.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-jobs-list">
          {jobs.map((job) => {
            const isExpired = job.end_date && job.end_date < today;
            const apps = applications[job.id] || [];
            return (
              <div key={job.id} className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg overflow-hidden">
                <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {job.title_de}
                      {isExpired && <span className="ml-2 text-xs text-red-400">abgelaufen</span>}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-1 text-xs text-[#8899AA]">
                      <span className={statusColor[job.status]}>{statusLabel[job.status]}</span>
                      <span>{job.publish_date}</span>
                      {job.end_date && <span>bis {job.end_date}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button onClick={() => toggleExpand(job.id)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A]">
                      <Users className="w-3 h-3 mr-1" />{apps.length > 0 ? apps.length : ""}
                      {expandedJob === job.id ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                    </Button>
                    <Button onClick={() => openEdit(job)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A]"><Pencil className="w-3 h-3" /></Button>
                    <Button onClick={() => handleDelete(job.id)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                {expandedJob === job.id && (
                  <div className="border-t border-[#3A4F60]/20 bg-[#0C1620]/50 p-4">
                    <h4 className="text-xs font-medium text-[#C5993A] mb-2">Bewerbungen / Kontaktaufnahmen</h4>
                    {apps.length === 0 ? <p className="text-[#3A4F60] text-xs">Keine Bewerbungen.</p> : (
                      <div className="space-y-2">
                        {apps.map((a) => (
                          <div key={a.id} className="bg-[#12202f] border border-[#3A4F60]/20 rounded-lg p-3">
                            <p className="text-white text-sm font-medium">{a.name}</p>
                            <div className="flex flex-wrap gap-3 text-xs text-[#8899AA] mt-1">
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.email}</span>
                              <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</span>
                              <span>{new Date(a.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                            </div>
                            {a.message && <p className="text-[#8899AA] text-xs mt-2 whitespace-pre-line">{a.message}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Job Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="job-dialog-desc">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{editing ? "Stellenangebot bearbeiten" : "Neues Stellenangebot"}</DialogTitle>
            <p id="job-dialog-desc" className="sr-only">Stellenangebot-Formular</p>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs text-[#8899AA] mb-1">Titel (DE) *</label><Input value={form.title_de} onChange={(e) => setForm({...form, title_de: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
              <div><label className="block text-xs text-[#8899AA] mb-1">Title (EN) *</label><Input value={form.title_en} onChange={(e) => setForm({...form, title_en: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-[#8899AA] mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full h-9 rounded-md border border-[#3A4F60]/50 bg-[#0C1620] text-white px-3 text-sm">
                  <option value="draft">Entwurf</option>
                  <option value="published">Veröffentlicht</option>
                </select>
              </div>
              <div><label className="block text-xs text-[#8899AA] mb-1">Veröffentlicht ab *</label><Input type="date" value={form.publish_date} onChange={(e) => setForm({...form, publish_date: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
              <div><label className="block text-xs text-[#8899AA] mb-1">Enddatum (optional)</label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
            </div>

            <div className="border-t border-[#3A4F60]/30 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm text-[#C5993A] font-medium">Inhaltsbereiche</h4>
                <Button onClick={addSection} variant="ghost" size="sm" className="text-[#C5993A] text-xs"><Plus className="w-3 h-3 mr-1" /> Bereich</Button>
              </div>
              {form.sections.map((sec, i) => (
                <div key={i} className="mb-4 bg-[#0C1620] border border-[#3A4F60]/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#8899AA]">Bereich {i + 1}</span>
                    {form.sections.length > 1 && <Button onClick={() => removeSection(i)} variant="ghost" size="sm" className="text-red-400 text-xs h-6"><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <Input placeholder="Überschrift DE" value={sec.title_de} onChange={(e) => updateSection(i, "title_de", e.target.value)} className="bg-[#12202f] border-[#3A4F60]/50 text-white text-xs" />
                    <Input placeholder="Heading EN" value={sec.title_en} onChange={(e) => updateSection(i, "title_en", e.target.value)} className="bg-[#12202f] border-[#3A4F60]/50 text-white text-xs" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Textarea placeholder="Inhalt DE" value={sec.content_de} onChange={(e) => updateSection(i, "content_de", e.target.value)} rows={4} className="bg-[#12202f] border-[#3A4F60]/50 text-white text-xs resize-none" />
                    <Textarea placeholder="Content EN" value={sec.content_en} onChange={(e) => updateSection(i, "content_en", e.target.value)} rows={4} className="bg-[#12202f] border-[#3A4F60]/50 text-white text-xs resize-none" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#8899AA] text-sm">Abbrechen</Button>
            <Button onClick={handleSave} disabled={!form.title_de || !form.title_en || !form.publish_date} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">
              {editing ? "Aktualisieren" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
